import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import type { AuditResponse, AuditRow } from "../../api/types";
import { useToasts } from "../../state/toasts";
import { Empty } from "../../ui/components";
import { dateTime } from "../../ui/format";

/**
 * Журнал действий (п. 24 ТЗ).
 *
 * Названия действий и разделы приходят с сервера — тем же словарём, по
 * которому строится выгрузка. Собственный словарь на этой стороне разошёлся бы
 * с ним при первом же новом действии.
 */
function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

const PAGE_SIZE = 100;

export function AuditPage() {
  const { notify } = useToasts();

  const [data, setData] = useState<AuditResponse | null>(null);
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("");
  const [actor, setActor] = useState("");
  const [from, setFrom] = useState(() => isoDay(new Date(Date.now() - 6 * 86400000)));
  const [to, setTo] = useState(() => isoDay(new Date()));
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);

  const query = useMemo(() => {
    const params = new URLSearchParams({
      from: `${from}T00:00:00`,
      to: `${to}T23:59:59`,
      limit: String(PAGE_SIZE)
    });
    if (search.trim()) params.set("search", search.trim());
    if (group) params.set("group", group);
    if (actor) params.set("actor", actor);
    return params;
  }, [from, to, search, group, actor]);

  // Смена фильтра начинает просмотр заново: «показать ещё» поверх других
  // условий подмешивало бы к списку чужие записи.
  useEffect(() => {
    setOffset(0);
  }, [query]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(query);
      params.set("offset", String(offset));

      const result = await api.get<AuditResponse>(`/api/admin/audit?${params.toString()}`);
      setData(result);
      setRows(current => (offset === 0 ? result.items : [...current, ...result.items]));
    } catch (error) {
      notify(error instanceof Error ? error.message : "Не удалось загрузить журнал", "error");
    } finally {
      setLoading(false);
    }
  }, [query, offset, notify]);

  useEffect(() => {
    const timer = setTimeout(() => void load(), 250);
    return () => clearTimeout(timer);
  }, [load]);

  const groups = data?.groups ?? {};
  const shown = rows.length;
  const total = data?.total ?? 0;

  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">Контроль</p>
          <h1>Журнал действий</h1>
        </div>
        <a className="btn btn-sm" href={`/api/admin/audit/export.csv?${query.toString()}`} download>
          Выгрузить
        </a>
      </div>

      <section className="panel">
        <header>
          <div>
            <h2>Что происходило</h2>
            <p>
              Записи только добавляются: изменить или удалить событие нельзя ни из панели, ни через API. Выгрузки
              персональных данных попадают сюда наравне с правками.
            </p>
          </div>
        </header>

        <div className="filters">
          <label className="field">
            <span>С</span>
            <input type="date" value={from} onChange={event => setFrom(event.target.value)} />
          </label>
          <label className="field">
            <span>По</span>
            <input type="date" value={to} onChange={event => setTo(event.target.value)} />
          </label>
          <label className="field">
            <span>Раздел</span>
            <select value={group} onChange={event => setGroup(event.target.value)}>
              <option value="">Все разделы</option>
              {Object.entries(groups).map(([code, title]) => (
                <option key={code} value={code}>
                  {title}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Сотрудник</span>
            <select value={actor} onChange={event => setActor(event.target.value)}>
              <option value="">Кто угодно</option>
              {(data?.actors ?? []).map(name => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Поиск</span>
            <input
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Действие, сотрудник или детали"
            />
          </label>
        </div>

        {loading && rows.length === 0 ? (
          <Empty>Загрузка…</Empty>
        ) : rows.length === 0 ? (
          <Empty>За период ничего не происходило.</Empty>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Время</th>
                    <th>Действие</th>
                    <th>Сотрудник</th>
                    <th>Заказ</th>
                    <th>Детали</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr key={row.id}>
                      <td className="num">{dateTime(row.at)}</td>
                      <td>
                        {row.title}
                        <br />
                        <small className="muted tiny">
                          {row.group ? `${groups[row.group] ?? row.group} · ` : ""}
                          {row.action}
                        </small>
                      </td>
                      <td className="tiny">{row.actor}</td>
                      <td className="num">{row.orderNumber ? `№${row.orderNumber}` : "—"}</td>
                      <td>
                        <code className="tiny">{JSON.stringify(row.details)}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="row-actions">
              <span className="tiny muted">
                Показано {shown} из {total}
              </span>
              {shown < total ? (
                <button
                  type="button"
                  className="btn btn-sm ml-auto"
                  disabled={loading}
                  onClick={() => setOffset(shown)}
                >
                  {loading ? "Загружаем…" : "Показать ещё"}
                </button>
              ) : null}
            </div>
          </>
        )}
      </section>
    </>
  );
}
